import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Cookie,
  FileCheck2,
  Mail,
  ReceiptText,
  RefreshCcw,
  ShieldCheck,
} from "lucide-react";

import { SiteFooter } from "@/features/sales/storefront/components/SiteFooter";
import { SiteHeader } from "@/features/sales/storefront/components/SiteHeader";
import {
  LEGAL_EFFECTIVE_DATE,
  LEGAL_LINKS,
  LEGAL_RESPONSIBLE,
} from "@/features/legal/legal-config";

const icons = [ShieldCheck, FileCheck2, RefreshCcw, ReceiptText, Cookie, Mail];

export const Route = createFileRoute("/legal/")({
  head: () => ({
    meta: [
      { title: "Centro legal | Socrattica" },
      {
        name: "description",
        content:
          "Privacidad, términos de compra, reembolsos, licencia, cookies y contacto de Socrattica.",
      },
    ],
  }),
  component: LegalCenterPage,
});

function LegalCenterPage() {
  const documents = LEGAL_LINKS.slice(1);

  return (
    <div className="flex min-h-screen min-w-0 flex-col overflow-x-hidden bg-white text-black">
      <SiteHeader />
      <main id="main-content" className="min-w-0 flex-1">
        <header className="border-b border-white/15 bg-black text-white">
          <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6 sm:py-20">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-white/65">
              Información clara antes y después de comprar
            </p>
            <h1 className="mt-3 max-w-4xl break-words text-5xl font-semibold tracking-[-0.06em] sm:text-7xl">
              Centro legal.
            </h1>
            <p className="mt-5 max-w-2xl text-sm leading-7 text-white/65 sm:text-base">
              Aquí puedes consultar, imprimir o enlazar las reglas que aplican al sitio, las compras
              de recursos digitales y el uso de tus datos.
            </p>
          </div>
        </header>

        <section className="border-b border-black/10">
          <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 sm:py-16">
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {documents.map((item, index) => {
                const Icon = icons[index] ?? FileCheck2;
                return (
                  <Link
                    key={item.to}
                    to={item.to}
                    className="group flex min-h-52 flex-col rounded-xl border border-black/15 bg-white p-6 transition hover:-translate-y-0.5 hover:border-black hover:shadow-[0_16px_50px_rgb(0,0,0,0.08)]"
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-black text-white">
                      <Icon className="h-4 w-4" />
                    </span>
                    <h2 className="mt-6 text-xl font-semibold tracking-[-0.035em]">{item.label}</h2>
                    <p className="mt-2 text-xs leading-5 text-black/55">{item.description}</p>
                    <span className="mt-auto flex items-center gap-2 pt-6 text-xs font-semibold">
                      Consultar
                      <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        <section className="bg-[#f7f6f1]">
          <div className="mx-auto grid max-w-7xl gap-8 px-4 py-12 sm:px-6 md:grid-cols-2 md:items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-black/55">
                Responsable
              </p>
              <h2 className="mt-2 text-3xl font-semibold tracking-[-0.04em]">
                {LEGAL_RESPONSIBLE.name}
              </h2>
              <p className="mt-3 max-w-xl text-sm leading-6 text-black/60">
                {LEGAL_RESPONSIBLE.capacity}, con operación en {LEGAL_RESPONSIBLE.country}. Vigencia
                documental: {LEGAL_EFFECTIVE_DATE}.
              </p>
            </div>
            <div className="rounded-xl border border-black/15 bg-white p-6">
              <p className="text-sm font-semibold">Contacto directo</p>
              <a
                href={`mailto:${LEGAL_RESPONSIBLE.email}`}
                className="mt-2 block break-all text-sm underline underline-offset-4"
              >
                {LEGAL_RESPONSIBLE.email}
              </a>
              <p className="mt-3 text-xs leading-5 text-black/55">
                Objetivo de primera respuesta: {LEGAL_RESPONSIBLE.responseTime}. Las solicitudes de
                datos personales siguen los plazos legales explicados en la política de privacidad.
              </p>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
